import { AxiosResponse, isAxiosError } from "axios";

interface DirectDownloadResponse {
  delivery: "direct";
  download_url: string;
  filename?: string;
}

const validateDownloadUrl = (url: string): string => {
  let parsedUrl: URL;

  try {
    parsedUrl = new URL(url);
  } catch {
    throw new Error("Invalid download URL.");
  }

  if (parsedUrl.protocol !== "https:" && parsedUrl.protocol !== "http:") {
    throw new Error("Invalid download URL protocol.");
  }

  return parsedUrl.toString();
};

const triggerDirectDownload = (downloadUrl: string, filename: string): void => {
  const safeUrl = validateDownloadUrl(downloadUrl);
  const link = document.createElement("a");

  link.href = safeUrl;
  link.download = filename;
  link.rel = "noopener noreferrer";

  document.body.appendChild(link);
  link.click();
  link.remove();
};
const parseDirectDownloadResponse = (
  response: AxiosResponse<ArrayBuffer>,
): DirectDownloadResponse | null => {
  const contentType = String(
    response.headers["content-type"] ?? "",
  ).toLowerCase();

  if (!contentType.includes("application/json")) {
    return null;
  }

  try {
    const payload = JSON.parse(
      new TextDecoder().decode(response.data),
    ) as Partial<DirectDownloadResponse>;
    if (
      payload.delivery === "direct" &&
      typeof payload.download_url === "string" &&
      payload.download_url.length > 0
    ) {
      return payload as DirectDownloadResponse;
    }
  } catch {
    // Not a direct download payload.
  }

  return null;
};

export const handleDirectDownloadResponse = (
  response: AxiosResponse<ArrayBuffer>,
): boolean => {
  const directDownload = parseDirectDownloadResponse(response);

  if (!directDownload) {
    return false;
  }

  const filename =
    typeof directDownload.filename === "string"
      ? directDownload.filename.trim() || "download"
      : "download";

  triggerDirectDownload(directDownload.download_url, filename);

  return true;
};

export const parseDownloadErrorMessage = (
  err: unknown,
  fallback: string,
): string => {
  if (!isAxiosError(err)) {
    if (err instanceof Error) {
      return err.message;
    }

    return fallback;
  }

  const data = err.response?.data;

  if (data instanceof ArrayBuffer) {
    const decoded = new TextDecoder().decode(data);

    try {
      const parsed = JSON.parse(decoded) as {
        detail?: string;
        message?: string;
      };

      return (parsed.detail ?? parsed.message ?? decoded) || fallback;
    } catch {
      return decoded || fallback;
    }
  }

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data) as { detail?: string; message?: string };

      return parsed.detail ?? parsed.message ?? data;
    } catch {
      return data || fallback;
    }
  }

  if (typeof data === "object" && data !== null) {
    const parsed = data as { detail?: string; message?: string };

    return parsed.detail ?? parsed.message ?? fallback;
  }

  return fallback;
};
